import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useAuth, usePermissions } from '@/features/auth';
import { formatDateTime } from '@/shared/lib/format';

import { usePendingApprovals } from '../api/approvals.queries';
import { DecisionDialog, type Decision } from './decision-dialog';

/**
 * A "waiting for approval" notice where the change lives (a manual FX rate,
 * channel fees), with the actions the viewer can take on the request right
 * there: Approve / Reject when the inbox says they may decide it, Withdraw
 * when they asked. The request is found in the pending inbox (approvals.read),
 * which already carries who asked, why, and whether the viewer may decide —
 * the same rules each type's endpoint enforces. Without approvals.read only
 * the message is shown.
 */
export function PendingApprovalNotice({
  approvalId,
  type,
  timeZone,
  children,
}: {
  approvalId: number;
  /** The approval type, for the inbox filter link (e.g. FxRateOverride). */
  type: string;
  /**
   * The zone the request time is shown in: the market the change belongs to, or Palestine time
   * when it belongs to none.
   */
  timeZone: string;
  /** What is waiting, e.g. "A manual rate is waiting for an Admin's approval." */
  children: ReactNode;
}) {
  const { can } = usePermissions();
  const { user: me } = useAuth();
  const canReadInbox = can('approvals.read');
  const pending = usePendingApprovals(canReadInbox);
  const item = pending.data?.find((candidate) => candidate.approval.id === approvalId);
  const [deciding, setDeciding] = useState<Decision | null>(null);

  const approval = item?.approval;
  const isRequester = approval !== undefined && me?.id === approval.requestedByUserId;
  const canDecide = item?.canDecide ?? false;

  return (
    <section
      aria-label="Waiting for approval"
      className="border-status-info-border bg-status-info-bg text-status-info flex flex-wrap items-start gap-x-3 gap-y-2 rounded-md border p-3 text-xs"
    >
      <div className="min-w-0 flex-1 space-y-0.5">
        <p>{children}</p>
        {approval && (
          <p className="text-foreground">
            Asked by {isRequester ? 'you' : (approval.requestedByName ?? 'a staff member')} on{' '}
            {formatDateTime(approval.requestedAtUtc, timeZone)}
            {approval.requestReason ? <> — &ldquo;{approval.requestReason}&rdquo;</> : null}
          </p>
        )}
        {item && !canDecide && !isRequester && item.cannotDecideReason && (
          <p>{item.cannotDecideReason}</p>
        )}
        {canReadInbox && (
          <Link
            to={`/approvals?type=${encodeURIComponent(type)}`}
            className="inline-block font-medium underline"
          >
            Open in Approvals
          </Link>
        )}
      </div>

      {approval && (canDecide || isRequester) && (
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          {canDecide && (
            <>
              <Button size="sm" className="h-7" onClick={() => setDeciding('approve')}>
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7"
                onClick={() => setDeciding('reject')}
              >
                Reject
              </Button>
            </>
          )}
          {isRequester && (
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => setDeciding('cancel')}
            >
              Withdraw request
            </Button>
          )}
        </div>
      )}

      {approval && deciding && (
        <DecisionDialog approval={approval} decision={deciding} onClose={() => setDeciding(null)} />
      )}
    </section>
  );
}
